<script setup lang="ts">
import OneNetworkConfig from '@/onenetwork/core/onenetwork.config'
import ListingListByAsset from '@/modules/realestate/components/ListingListByAsset.vue'
import AssetHistoryList from '@/modules/realestate/components/AssetHistoryList.vue'
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import {
  useAssetDetailStore,
  AssetDetailActionType,
} from '@/modules/realestate/stores/realestate.detail.store'
import '@/modules/realestate/assets/scss/listing.scss'
import { Icon } from '@iconify/vue'
import { GoogleMap, Polygon } from 'vue3-google-map'

interface Props {
  id: number
  approveForm?: boolean
}

const assetDetailStore = useAssetDetailStore()
const center = ref('')
const triangleCoords = ref([])

const props = defineProps<Props>()

const listingDetail = computed(() => {
  try {
    return assetDetailStore.assets[props.id]?.data
  } catch (e) {
    return null
  }
})
const fetched = computed(() => {
  return assetDetailStore.assets[props.id]?.action_type
})

const requestFetchDetail = async () => {
  try {
    await assetDetailStore.fetchAssetDetail(props.id)
  } catch (e) {
    console.error(e)
  }
}

watch(
  () => listingDetail.value,
  (newValue) => {
    if (newValue && newValue.id) {
      nextTick(() => {
        center.value = {
          lat: newValue.centroid.coordinates[1],
          lng: newValue.centroid.coordinates[0],
        }
        triangleCoords.value = newValue.wgs84_coordinates?.coordinates[0]?.map((point: any) => ({
          lat: point[1],
          lng: point[0],
        }))
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  requestFetchDetail()
})
</script>

<template>
  <template v-if="AssetDetailActionType.FETCHING === fetched">
    <Card>
      <template #content>
        <div class="flex gap-3">
          Đang tải dữ liệu <Icon icon="eos-icons:three-dots-loading" width="24" height="24" />
        </div>
      </template>
    </Card>
  </template>
  <template v-if="AssetDetailActionType.FETCH_FAILED === fetched">
    <Card class="bg-red-300">
      <template #content>
        <div class="flex flex-col gap-3 items-center">
          <Icon icon="fluent-color:error-circle-16" width="50" />
          <span class="font-300 text-[16px]">Lỗi khi tải dữ liệu này</span>
        </div>
      </template>
    </Card>
  </template>
  <template v-if="AssetDetailActionType.FETCHED === fetched">
    <Panel class="mb-5 pb-5">
      <template #header>
        <div class="flex items-center justify-between w-full">
          <h3 class="text-base font-semibold p-panel-title">
            Mã BDS: #{{ listingDetail?.id || '' }}
          </h3>
        </div>
      </template>
      <div class="profile w-full sm:flex sm:flex-col md:grid md:grid-cols-3 gap-3">
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Khu vực:</label>
            <div class="flex-1 font-bold">
              {{ listingDetail?.ward?.name || '' }}, {{ listingDetail?.province?.name || '' }}
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">địa chỉ:</label>
            <div class="flex-1 font-bold">
              {{ listingDetail?.address || '' }}
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Tờ thửa:</label>
            <div class="flex-1">
              <span class="font-italic">
                Thửa <span class="font-bold">{{ listingDetail?.plot_number || '--' }}</span>
                {{ ' - ' }}
                Tờ bản đồ
                <span class="font-bold">{{ listingDetail?.map_sheet_number || '--' }}</span>
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Diện tích:</label>
            <div class="flex-1">
              <span class="font-bold">{{ listingDetail?.area || '--' }}</span>
            </div>
          </div>
        </div>
        <div class="col-span-2">
          <div class="border w-full h-full">
            <GoogleMap
              :api-key="OneNetworkConfig.map.apiKey"
              :center="center"
              :zoom="19"
              style="width: 100%; height: 200px"
            >
              <Polygon
                :options="{
                  paths: triangleCoords,
                  strokeColor: '#FF0000',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: '#FF0000',
                  fillOpacity: 0.35,
                }"
              />
            </GoogleMap>
          </div>
        </div>
      </div>
    </Panel>
    <Tabs value="0" scrollable>
      <TabList>
        <Tab value="0">Lịch sử cập nhật</Tab>
        <Tab value="1">Danh sách tin đăng</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <p class="m-0">
            <AssetHistoryList :id="listingDetail?.id" v-if="listingDetail?.id" />
          </p>
        </TabPanel>
        <TabPanel value="1">
          <p class="m-0">
            <ListingListByAsset :id="listingDetail?.id" v-if="listingDetail?.id" />
          </p>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </template>
</template>

<style scoped></style>
